import {find, get} from 'lodash';
import {getArray, getBoolean, getInteger, getString} from "Core/helpers/data";
import {SORT_ORDER} from "Core/const/global";
import {pagination_default_per_page} from "Config/app";
import {SignalQualityDataObject} from './dataObjects';

/**
 * Unique Redux store key associated to this reducer
 * IMPORTANT: All reducers must export this value!
 * @type {string}
 */
export const reducerStoreKey = 'studies';

// Define reducer types handled by this reducers
export const REDUCER_TYPES = {
	RESET: '@studies/reset',
	SET_LIST_DATA: '@studies/set_list_data',
	CLEAR_LIST_DATA: '@studies/clear_list_data',
	SET_LIST_SELECTION: '@studies/set_list_selection',
	
	TOGGLE_ADVANCED_SEARCH: '@studies/toggle_advanced_search',
	
	SET_SIGNAL_QUALITY_DATA: '@studies/set_signal_quality_data',
	CLEAR_SIGNAL_QUALITY_DATA: '@studies/clear_signal_quality_data',
	SET_SIGNAL_QUALITY_DATA_FOR_STUDY: '@studies/set_signal_quality_data_for_study',
};

// Define action creators for all reducer types
export const actionCreators = {
	reset: () => ({type: REDUCER_TYPES.RESET}),
	/**
	 * @param {IoJsonFetchResponseObject} studiesListData
	 * @return {{type: string, listData: IoJsonFetchResponseObject}}
	 */
	setStudiesListData: studiesListData => ({
		type: REDUCER_TYPES.SET_LIST_DATA, 
		listData: studiesListData
	}),
	clearStudiesListData: () => ({type: REDUCER_TYPES.CLEAR_LIST_DATA}),
	setStudiesListSelection: selection => ({type: REDUCER_TYPES.SET_LIST_SELECTION, selection}),
	
	toggleAdvancedSearch: () => ({type: REDUCER_TYPES.TOGGLE_ADVANCED_SEARCH}),

	/**
	 * @param {?SignalQualityDataObject[]|undefined} signalQualityData
	 * @return {{type: string, signalQualityData: ?SignalQualityDataObject[]|undefined}}
	 */
	setSignalQualityData: signalQualityData => ({type: REDUCER_TYPES.SET_SIGNAL_QUALITY_DATA, signalQualityData}),
	clearSignalQualityData: () => ({type: REDUCER_TYPES.CLEAR_SIGNAL_QUALITY_DATA}),
	/**
	 * @param {string} studyId
	 * @param {?SignalQualityNightDataObject[]|undefined} studyNights
	 * @return {{type: string, studyId: string, studyNights: ?SignalQualityNightDataObject[]|undefined}}
	 */
	setSignalQualityDataForStudy: (studyId, studyNights) => ({
		type: REDUCER_TYPES.SET_SIGNAL_QUALITY_DATA_FOR_STUDY, studyId, studyNights
	}),
};

/**
 * Initial reducer state
 * IMPORTANT: All reducers must export initial state object!
 * @type {Object<string, any>}
 */
export const initialState = {
	/** @type {IoJsonFetchResponseObject} */
	listData: null,
	/** @type {StudiesListItemDataObject[]} */
	listSelection: [],
	/** @type {boolean} Flag that specifies if advanced search should be visible */
	advancedSearchVisible: false,
	/** @type {?SignalQualityDataObject[]|undefined} */
	signalQualityData: null,
};

// Reducer function
const reducer = (state = {...initialState}, action) => {
	switch (action.type) {
		case REDUCER_TYPES.RESET: return { ...initialState };

		case REDUCER_TYPES.SET_LIST_DATA: return {...state, listData: action.listData};
		case REDUCER_TYPES.CLEAR_LIST_DATA: return {...state, listData: initialState.listData};
		case REDUCER_TYPES.SET_LIST_SELECTION: return {...state, listSelection: getArray(action, 'selection')};

		case REDUCER_TYPES.TOGGLE_ADVANCED_SEARCH: return {...state, advancedSearchVisible: !state.advancedSearchVisible};
		
		case REDUCER_TYPES.SET_SIGNAL_QUALITY_DATA: return {...state, signalQualityData: action.signalQualityData};
		case REDUCER_TYPES.CLEAR_SIGNAL_QUALITY_DATA: return {...state, signalQualityData: initialState.signalQualityData};
		case REDUCER_TYPES.SET_SIGNAL_QUALITY_DATA_FOR_STUDY: return {
			...state, 
			signalQualityData: (
				Array.isArray(state.signalQualityData) ?
					state.signalQualityData
						.map(i => i.studyId === action.studyId ? {...i, studyNights: action.studyNights} : i)
					:
					[new SignalQualityDataObject(action.studyId, action.studyNights)]
			),
		};

		default: return state;
	}
};

// Selectors
export const selectors = {
	getStudiesList: state => get(state, [reducerStoreKey, 'listData', 'data']),
	getStudiesListPagination: state => ({
		perPage: getInteger(state, [reducerStoreKey, 'listData', 'perPage'], pagination_default_per_page),
		pageNo: getInteger(state, [reducerStoreKey, 'listData', 'pageNo'], 1),
		totalRows: getInteger(state, [reducerStoreKey, 'listData', 'totalResults']),
		isLastPage: getBoolean(state, [reducerStoreKey, 'listData', 'isLastPage'])
	}),
	getStudiesListSort: state => ({
		sortBy: getString(state, [reducerStoreKey, 'listData', 'sortBy'],'creationDate'),
		sortDir: getString(state, [reducerStoreKey, 'listData', 'sortDir'], SORT_ORDER.DESC)
	}),
	getStudiesListFilter: state => get(state, [reducerStoreKey, 'listData', 'filter'], null),
	getStudiesListSelection: state => getArray(state, [reducerStoreKey, 'listSelection']),

	isStudiesListAdvancedSearchVisible: state => getBoolean(state, [reducerStoreKey, 'advancedSearchVisible']),
	
	getStudySignalQualityData: (state, studyId) => (
		!!get(state, [reducerStoreKey, 'signalQualityData']) ?
			(
				find(get(state, [reducerStoreKey, 'signalQualityData']), {studyId}) || 
				new SignalQualityDataObject(studyId, [])
			) 
			:
			get(state, [reducerStoreKey, 'signalQualityData'])
	),
};

export default reducer;